import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import '../styles/NotPageForm.css';
import ButtonRegular from './ui/ButtonRegular';
import { DELIVERY_OPTIONS } from '../utils/formatDelivery'; 

const NotPageForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [products, setProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [errorProducts, setErrorProducts] = useState('');
    const [closed, setClosed] = useState(false);

    const [deliveryMethod, setDeliveryMethod] = useState(DELIVERY_OPTIONS[0]?.value || '');
    const [formNumber, setFormNumber] = useState(null);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        street: '',
        building: '',
        apartment: '',
        city: '',
        postalCode: '',
        vatInvoice: false,
        companyName: '',
        companyAddress: '',
        taxId: '',
        additionalInfo: '',
        acceptTerms: false,
        acceptPrivacy: false,
    });

    const [useDifferentShipping, setUseDifferentShipping] = useState(false);
    const [shippingData, setShippingData] = useState({
        street: '',
        building: '',
        apartment: '',
        city: '',
        postalCode: '',
    });

    const [submitted, setSubmitted] = useState(false);
    const [submitError, setSubmitError] = useState('');
    
    const [paymentLoading, setPaymentLoading] = useState(false); 

    const [regulationAccepted, setRegulationAccepted] = useState(false);

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/forms/${id}`);
                setProducts(res.data.products || []);
                setClosed(res.data.status === 'closed');
                if (res.data.deliveryMethod) setDeliveryMethod(res.data.deliveryMethod);
                setFormNumber(res.data.formNumber);
            } catch (err) {
                setErrorProducts('Błąd podczas ładowania produktów.');
            } finally {
                setLoadingProducts(false);
            }
        };
        fetchProducts();
    }, [id]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleShippingChange = (e) => {
        const { name, value } = e.target;
        setShippingData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError('');

        if (
            !formData.firstName.trim() ||
            !formData.lastName.trim() ||
            !formData.email.trim() ||
            !formData.phone.trim() ||
            !formData.street.trim() ||
            !formData.building.trim() ||
            !formData.city.trim() ||
            !formData.postalCode.trim()
        ) {
            setSubmitError('Proszę wypełnić wszystkie obowiązkowe pola.');
            return;
        }

        if (formData.vatInvoice && !formData.taxId.trim()) {
            setSubmitError('Proszę podać NIP dla faktury VAT.');
            return;
        }

        if (!formData.acceptTerms || !formData.acceptPrivacy) {
            setSubmitError('Musisz zaakceptować regulamin i politykę prywatności.');
            return;
        }

        if (useDifferentShipping) {
            if (!shippingData.street.trim() || !shippingData.building.trim() || !shippingData.city.trim() || !shippingData.postalCode.trim()) {
                setSubmitError('Proszę wypełnić wszystkie pola adresu wysyłkowego.');
                return;
            }
        }

        const finalShippingData = useDifferentShipping
            ? shippingData
            : {
                    street: formData.street,
                    building: formData.building,
                    apartment: formData.apartment,
                    city: formData.city,
                    postalCode: formData.postalCode,
                };

        try {
            await axios.put(`${process.env.REACT_APP_API_URL}/api/forms/answear/${id}`, {
                products,
                deliveryMethod,
                customerData: formData,
                shippingData: finalShippingData,
                closeForm: true,
            });
            setSubmitted(true);
        } catch (err) {
            setSubmitError('Błąd podczas składania zamówienia. Spróbuj ponownie.');
        }
    };

const handlePayment = async () => {
    if (!formNumber) {
        setSubmitError('Błąd: Nie odnaleziono numeru zamówienia. Odśwież stronę.');
        return;
    }
    
    if (paymentLoading || !regulationAccepted) return;

    setPaymentLoading(true);
    setSubmitError('');
    
    try {
        const res = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/payment/p24/create-public-transaction`,
            {
                orderNumber: formNumber,
                regulationAccept: true,
                totalPriceFromFront: grandTotal
            }
        );

        if (!res.data?.success || !res.data.redirectUrl) {
            throw new Error(res.data?.message || 'Nie udało się utworzyć płatności');
        }

        window.location.href = res.data.redirectUrl;

    } catch (err) {
        console.error('Błąd inicjacji płatności na froncie:', err);
        setSubmitError(err.response?.data?.message || err.message || 'Błąd inicjacji płatności');
        setPaymentLoading(false);
    }
};

    const currentDelivery = DELIVERY_OPTIONS.find(opt => opt.value === deliveryMethod);
    const deliveryCost = parseFloat(currentDelivery?.price || 0);
    const totalPrice = products.reduce((sum, p) => sum + (p.price || 0), 0);
    const grandTotal = totalPrice + deliveryCost;

    if (submitted || closed) {
        return (
            <div className="form-container">
                <div className="form-content">
                    <h1 className="form-title">FORMULARZ ZAMÓWIENIA</h1>
                    <h3 className="form-subtitle">Podgląd zamówienia nr: {formNumber ? String(formNumber).padStart(4, '0') : ''}</h3>
                    <section className="form-section">
                        <h2>Podsumowanie zamówienia</h2>
                        {loadingProducts ? (
                            <p>Ładowanie produktów...</p>
                        ) : errorProducts ? (
                            <p className="error-text">{errorProducts}</p>
                        ) : products.length === 0 ? (
                            <p>Brak produktów w formularzu.</p>
                        ) : (
                            <table className="products-table">
                                <thead>
                                    <tr>
                                        <th>Produkt</th>
                                        <th style={{ textAlign: 'right' }}>Cena (PLN)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {products.map((p, i) => (
                                        <tr key={i} className={i % 2 === 0 ? 'table-row-even' : 'table-row-odd'}>
                                            <td>{p.name}</td>
                                            <td style={{ textAlign: 'right' }}>{p.price.toFixed(2)}</td>
                                        </tr>
                                    ))}
                                    <tr className="table-total-row">
                                        <td>Razem za produkty</td>
                                        <td style={{ textAlign: 'right' }}>{totalPrice.toFixed(2)}</td>
                                    </tr>
                                    <tr className="table-total-row">
                                        <td>Koszt dostawy</td>
                                        <td style={{ textAlign: 'right' }}>{deliveryCost.toFixed(2)}</td>
                                    </tr>
                                    <tr className="table-total-row">
                                        <td>Łącznie do zapłaty</td>
                                        <td style={{ textAlign: 'right' }}>{grandTotal.toFixed(2)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        )}
                    </section>
                    
                    {submitError && <p className="error-text" style={{ textAlign: 'center' }}>{submitError}</p>}
                    
                    <p style={{ textAlign: 'center', margin: '20px 0' }}>
                        Informujemy, że w przypadku gdy do 3 dni od złożenia zamówienia nie otrzymamy wpłaty,
                        zamówienie zostanie anulowane.
                    </p>
                    <div className="form-buttons">
                        <ButtonRegular onClick={() => navigate('/')}>Przejście na stronę główną</ButtonRegular>
                        <div className="summary-actions">
                            <div className="p24-terms-wrapper">
    <input
        type="checkbox"
        id="p24-regulation"
        checked={regulationAccepted}
        onChange={e => setRegulationAccepted(e.target.checked)}
        className="p24-checkbox"
    />
    <label htmlFor="p24-regulation" className="p24-label">
        Oświadczam, że zapoznałem się z{' '}
        <a href="https://www.przelewy24.pl/regulamin" target="_blank" rel="noopener noreferrer">
            regulaminem
        </a>{' '}
        oraz{' '}
        <a href="https://www.przelewy24.pl/obowiazek-informacyjny-rodo-platnicy" target="_blank" rel="noopener noreferrer">
            obowiązkiem informacyjnym
        </a>{' '}
        serwisu Przelewy24.
    </label>
</div>
                            <ButtonRegular
                                onClick={handlePayment}
                                disabled={paymentLoading || !regulationAccepted} 
                                className={`p24-button ${(paymentLoading || !regulationAccepted) ? 'disabled' : ''}`}
                            >
                                {paymentLoading ? (
                                    'Przekierowanie do płatności…'
                                ) : (
                                    <>
                                        <img
                                            src="/p24/Przelewy24_logo.png"
                                            alt="Przelewy24"
                                            className="p24-logo-img"
                                            style={{ height: '20px', marginRight: '8px', verticalAlign: 'middle' }}
                                        />
                                        <span>Opłać zamówienie</span>
                                    </>
                                )}
                            </ButtonRegular>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="form-container">
            <div className="form-content">
                <h1 className="form-title">FORMULARZ ZAMÓWIENIA</h1>
                <h3 className="form-subtitle">Podgląd zamówienia nr: {formNumber ? String(formNumber).padStart(4, '0') : ''}</h3>
                <p className="info-text">
                    Dziękujemy za zakupy! Prosimy o sprawdzenie poprawności danych przed złożeniem zamówienia.
                </p>

                <section className="form-section">
                    <h2>Podsumowanie zamówienia</h2>
                    {loadingProducts ? (
                        <p>Ładowanie produktów...</p>
                    ) : errorProducts ? (
                        <p className="error-text">{errorProducts}</p>
                    ) : products.length === 0 ? (
                        <p>Brak produktów w formularzu.</p>
                    ) : (
                        <table className="products-table">
                            <thead>
                                <tr>
                                    <th className="table-header-price">Produkt</th>
                                    <th className="table-header-price" style={{ textAlign: 'right' }}>Cena (PLN)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {products.map((p, i) => (
                                    <tr key={i} className={i % 2 === 0 ? 'table-row-even' : 'table-row-odd'}>
                                        <td>{p.name}</td>
                                        <td style={{ textAlign: 'right' }}>{p.price.toFixed(2)}</td>
                                    </tr>
                                ))}
                                <tr className="table-total-row">
                                    <td>Razem za produkty</td>
                                    <td style={{ textAlign: 'right' }}>{totalPrice.toFixed(2)}</td>
                                </tr>
                                <tr className="table-total-row">
                                    <td>Koszt dostawy</td>
                                    <td style={{ textAlign: 'right' }}>{deliveryCost.toFixed(2)}</td>
                                </tr>
                                <tr className="table-total-row">
                                    <td>Łącznie do zapłaty</td>
                                    <td style={{ textAlign: 'right' }}>{grandTotal.toFixed(2)}</td>
                                </tr>
                            </tbody>
                        </table>
                    )}
                </section>

                <section className="form-section">
                    <h2>Sposób dostawy</h2>
                    <p>
                        <strong>{currentDelivery?.label || 'Nie wybrano'}</strong> –{' '}
                        {currentDelivery?.price?.toFixed(2) || '0.00'} PLN
                    </p>
                </section>

                <form onSubmit={handleSubmit}>
                    <section className="form-section">
                        <h2>Dane osobowe</h2>
                        <label className="form-label">
                            Imię
                            <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required className="form-input" />
                        </label>
                        <label className="form-label">
                            Nazwisko
                            <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required className="form-input" />
                        </label>
                        <label className="form-label">
                            Email
                            <input type="email" name="email" value={formData.email} onChange={handleChange} required className="form-input" />
                        </label>
                        <label className="form-label">
                            Nr telefonu
                            <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className="form-input" />
                        </label>
                    </section>

                    <section className="form-section">
                        <h2>Dane adresowe</h2>
                        <label className="form-label">
                            Ulica
                            <input type="text" name="street" value={formData.street} onChange={handleChange} required className="form-input" />
                        </label>
                        <label className="form-label">
                            Numer budynku
                            <input type="text" name="building" value={formData.building} onChange={handleChange} required className="form-input" />
                        </label>
                        <label className="form-label">
                            Numer lokalu
                            <input type="text" name="apartment" value={formData.apartment} onChange={handleChange} className="form-input" />
                        </label>
                        <label className="form-label">
                            Miasto
                            <input type="text" name="city" value={formData.city} onChange={handleChange} required className="form-input" />
                        </label>
                        <label className="form-label">
                            Kod pocztowy
                            <input type="text" name="postalCode" value={formData.postalCode} onChange={handleChange} required className="form-input" />
                        </label>

                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={useDifferentShipping}
                                onChange={() => setUseDifferentShipping(prev => !prev)}
                            />
                            Dane wysyłkowe inne niż dane adresowe
                        </label>

                        {useDifferentShipping && (
                            <div className="shipping-fields">
                                <label className="form-label">
                                    Ulica
                                    <input type="text" name="street" value={shippingData.street} onChange={handleShippingChange} required className="form-input" />
                                </label>
                                <label className="form-label">
                                    Numer budynku
                                    <input type="text" name="building" value={shippingData.building} onChange={handleShippingChange} required className="form-input" />
                                </label>
                                <label className="form-label">
                                    Numer lokalu
                                    <input type="text" name="apartment" value={shippingData.apartment} onChange={handleShippingChange} className="form-input" />
                                </label>
                                <label className="form-label">
                                    Miasto
                                    <input type="text" name="city" value={shippingData.city} onChange={handleShippingChange} required className="form-input" />
                                </label>
                                <label className="form-label">
                                    Kod pocztowy
                                    <input type="text" name="postalCode" value={shippingData.postalCode} onChange={handleShippingChange} required className="form-input" />
                                </label>
                            </div>
                        )}
                    </section>

                    <section className="form-section">
                        <h2>Faktura VAT</h2>
                        <label className="checkbox-label">
                            <input type="checkbox" name="vatInvoice" checked={formData.vatInvoice} onChange={handleChange} />
                            Chcę otrzymać fakturę VAT
                        </label>
                        {formData.vatInvoice && (
                            <>
                                <label className="form-label">
                                    Nazwa firmy
                                    <input type="text" name="companyName" value={formData.companyName} onChange={handleChange} required className="form-input" />
                                </label>
                                <label className="form-label">
                                    Adres firmy
                                    <input type="text" name="companyAddress" value={formData.companyAddress} onChange={handleChange} required className="form-input" />
                                </label>
                                <label className="form-label">
                                    NIP
                                    <input type="text" name="taxId" value={formData.taxId} onChange={handleChange} required className="form-input" />
                                </label>
                            </>
                        )}
                    </section>

                    <section className="form-section">
                        <h2>Informacje dodatkowe</h2>
                        <label className="form-label">
                            <textarea
                                name="additionalInfo"
                                value={formData.additionalInfo}
                                onChange={handleChange}
                                className="form-textarea"
                                placeholder="Tutaj możesz wpisać dodatkowe informacje dotyczące zamówienia..."
                            />
                        </label>
                    </section>

                    <section className="form-section">
                        <label className="checkbox-label">
                            <input type="checkbox" name="acceptTerms" checked={formData.acceptTerms} onChange={handleChange} required />
                            Akceptuję <a href="/pdf/regulamin.pdf" target="_blank" rel="noreferrer">regulamin</a>
                        </label>
                        <label className="checkbox-label">
                            <input type="checkbox" name="acceptPrivacy" checked={formData.acceptPrivacy} onChange={handleChange} required />
                            Akceptuję <a href="/pdf/politykaprywatnosci.pdf" target="_blank" rel="noreferrer">politykę prywatności</a>
                        </label>
                    </section>

                    {submitError && <p className="error-text">{submitError}</p>}

                    <div className="form-buttons">
                        <ButtonRegular type="submit">Złóż zamówienie</ButtonRegular>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default NotPageForm;